'use client'

import { compactClassName } from '@/components/homepage-october/compact'
import * as React from 'react'
import { KNOWN_ROUTES } from '@/lib/homepage-2026/links'

export type DestinationGates = { collections: string[]; hubs: string[] }
const Context = React.createContext<DestinationGates | null>(null)

export function destinationIsActive(href: string, gates: DestinationGates): boolean {
  if (!href.startsWith('/')) return true
  const path = href.split(/[?#]/)[0]
  const parts = path.split('/').filter(Boolean)
  if (parts[0] === 'collections' && parts.length === 2) return gates.collections.includes(parts[1])
  if (parts.length === 1 && !KNOWN_ROUTES.has(parts[0]) && !['llms.txt', 'sitemap.xml'].includes(parts[0])) return gates.hubs.includes(parts[0])
  return true
}

export function DestinationProvider({ gates, children }: { gates: DestinationGates; children: React.ReactNode }) {
  return <Context.Provider value={gates}>{children}</Context.Provider>
}

/** Read the existing visible link words without duplicating icon decoration. */
function linkWords(node: React.ReactNode): string {
  if (typeof node === 'string' || typeof node === 'number') return String(node)
  if (Array.isArray(node)) return node.map(linkWords).join(' ')
  if (React.isValidElement<{ children?: React.ReactNode; alt?: string }>(node)) return linkWords(node.props.children) || node.props.alt || ''
  return ''
}

/** Distinguish a reference guide, collection and category without changing visible copy. */
function destinationLabel(href: string, label: string): string | undefined {
  if (href.startsWith('/wiki/')) return `${label} — reference guide`
  if (/^Under \$[\d,]+$/.test(label) && href.startsWith('/collections/ice-makers-under-')) return `${label} — ice makers`
  if (label === 'ENERGY STAR') return href === '/collections/energy-star-appliances' ? 'ENERGY STAR appliances' : 'ENERGY STAR refrigerators'
  if (label === 'Refrigerator water filters' && href === '/filters') return `${label} — filter directory`
  if (label === 'Shop all mini fridges' && href === '/mini-fridges') return `${label} — category guide`
  return undefined
}

/** Preserve the production rule: inactive collections and hubs are never linked. */
export function DestinationLink({ href, children, ...props }: React.ComponentProps<'a'>) {
  const gates = React.useContext(Context)
  if (href && gates && !destinationIsActive(href, gates)) return null
  const fin = /^\/products\/.*-(fin\d+)(?:[?#]|$)/i.exec(href || '')?.[1].replace(/^fin/i, 'FIN-')
  const label = props['aria-label'] || linkWords(children).replace(/\s+/g, ' ').trim()
  const aria = fin && props['aria-label'] ? `${props['aria-label']} — ${fin}` : destinationLabel(href || '', label)
  return <a href={href} {...props} className={compactClassName(props.className)} aria-label={aria || props['aria-label']}>{children}{fin && !props['aria-label'] && !props['aria-hidden'] ? <span className={compactClassName("sr-only")}> {fin}</span> : null}{' '}</a>
}
